import type { CategoryContent } from '../row.js';

/** Literature & Poetry, second chunk. Correct option first; rotated at build. */
export const MORE_LITERATURE: CategoryContent = {
  msc: [
    [
      'What is free indirect discourse?',
      [
        'Third-person narration that takes on a character\'s idiom and perspective without quotation',
        'Dialogue reported without attribution to a speaker, leaving the reader to infer who is talking',
        'A first-person narrator addressing the reader directly',
        'Interior monologue presented without punctuation or syntax',
      ],
      0,
      'Austen is usually credited with perfecting it in English. Unpunctuated interior monologue is closer to stream of consciousness, a later and different device.',
    ],
    [
      'Which metre dominates English dramatic verse of the Elizabethan period?',
      [
        'Iambic pentameter',
        'Dactylic hexameter',
        'Trochaic tetrameter',
        'Anapaestic trimeter',
      ],
      0,
      'Blank verse - unrhymed iambic pentameter - which Surrey introduced and Marlowe made speakable.',
    ],
    [
      'The Petrarchan sonnet divides how?',
      [
        'An octave and a sestet, with the turn between them',
        'Three quatrains and a closing couplet',
        'Two septets with a shared refrain',
        'Fourteen unrhymed lines with no internal division',
      ],
      0,
      'The three-quatrains-and-couplet shape is the English or Shakespearean form, and it puts the turn in a different place.',
    ],
    [
      'What does the term "bildungsroman" designate?',
      [
        'A novel tracing the formation of its protagonist from youth to maturity',
        'A novel constructed entirely from letters between characters',
        'A novel set in a single day in one city',
        'A novel whose narrator is revealed to be unreliable at its close',
      ],
      0,
      'Wilhelm Meister is the type case. A novel built from letters is the epistolary form, and both were being written at the same time.',
    ],
    [
      'Who wrote "The Tale of Genji"?',
      [
        'Murasaki Shikibu',
        'Sei Shonagon, whose Pillow Book belongs to the same court',
        'Ki no Tsurayuki, who compiled the Kokinshu',
        'Yoshida Kenko, author of Essays in Idleness',
      ],
      0,
      'Murasaki Shikibu wrote it in the early eleventh century; it is often called the first psychological novel. Sei Shonagon, Ki no Tsurayuki and Yoshida Kenko all belong to the same Heian and early-medieval Japanese courtly literary tradition, which is why the names are easy to mix up without knowing the specific work each is known for.',
    ],
    [
      'What is prosopopoeia?',
      [
        'A figure in which an absent or inanimate thing is given a voice',
        'The substitution of an attribute for the thing itself',
        'An address to an absent person or abstraction',
        'The repetition of a word at the start of successive clauses',
      ],
      0,
      'Apostrophe is the address, metonymy the substitution, anaphora the repetition - all commonly conflated with it.',
    ],
    [
      'Which movement is Cesar Vallejo associated with?',
      [
        'Latin American avant-garde poetry, adjacent to but distinct from surrealism',
        'The Spanish Generation of 1927, alongside Lorca, Alberti and Cernuda in Madrid',
        'Brazilian modernismo of the 1920s',
        'The Chilean Creacionismo of Huidobro',
      ],
      0,
      'Trilce (1922) predates the surrealist manifesto and breaks Spanish syntax more violently than anything the movement produced.',
    ],
    [
      'What is the Russian formalist concept of ostranenie?',
      [
        'Defamiliarisation: art renders the habitual strange so it can be perceived again',
        'The estrangement of the reader from an unreliable narrator',
        'The separation of story material from its arrangement in the telling',
        'The alienation effect achieved by breaking dramatic illusion',
      ],
      0,
      'Shklovsky, 1917. The story-versus-arrangement distinction is fabula and syuzhet, and the alienation effect is Brecht.',
    ],
    [
      'The "Négritude" movement was founded principally by whom?',
      [
        'Aime Cesaire, Leopold Sedar Senghor and Leon Damas',
        'Chinua Achebe and Wole Soyinka',
        'Frantz Fanon and Albert Memmi',
        'Claude McKay and Langston Hughes',
      ],
      0,
      'Paris in the 1930s. The Harlem Renaissance writers influenced it, Fanon criticised it, and the Anglophone Africans came later and were sceptical.',
    ],
    [
      'What does "in medias res" describe?',
      [
        'Beginning a narrative in the middle of the action rather than at its start',
        'A narrative that returns to its opening scene at the close',
        'A digression that interrupts the main action',
        'A subplot mirroring the structure of the main plot',
      ],
      0,
      'Horace\'s prescription, and the Iliad and Odyssey both do it - which is why he could prescribe it.',
    ],
  ],
  phd: [
    [
      'What does Auerbach\'s "Mimesis" argue about biblical and Homeric narrative?',
      [
        'Homeric style externalises everything, while biblical narrative is "fraught with background"',
        'Both traditions share a paratactic syntax reflecting oral composition',
        'Biblical narrative is more concerned with individual psychology than Greek epic',
        'Homeric narrative subordinates description to plot in a way scripture does not',
      ],
      0,
      'The Odysseus scar chapter. The claim about foregrounding versus depth is doing all the work, and it launched a whole method.',
    ],
    [
      'The concept of "the death of the author" is associated with whom, and asserts what?',
      [
        'Barthes: a text\'s meaning is not secured by authorial intention',
        'Foucault: authorship is a historically contingent function of discourse',
        'Derrida: no text has a determinate meaning at all',
        'Wimsatt and Beardsley: appeal to intention is a critical fallacy',
      ],
      0,
      'All four are real positions and often confused. Foucault\'s "What Is an Author?" is the answer to Barthes, not a restatement of him.',
    ],
    [
      'What is a "cento"?',
      [
        'A poem assembled entirely from lines taken from other poems',
        'A hundred-line verse form of Provencal origin',
        'A poem in which each line begins with successive letters of the alphabet',
        'A commentary written in the margins of a classical text',
      ],
      0,
      'Late antique poets built whole Christian narratives out of Virgil this way. It is also why the form keeps being rediscovered as avant-garde.',
    ],
    [
      'The Sanskrit concept of rasa refers to what?',
      [
        'The aesthetic emotion realised in an audience by a work',
        'The metrical structure of a verse in classical prosody',
        'The moral instruction a drama is required to convey',
        'The formal division of a play into acts and scenes',
      ],
      0,
      'Abhinavagupta\'s account of how it is produced and received is the sophisticated version, and it long predates any European reception theory.',
    ],
    [
      'What distinguishes the picaresque from the bildungsroman?',
      [
        'The picaro is episodic and unchanged by experience; the bildungsroman protagonist is formed by it',
        'The picaresque is always narrated in the third person and never the first, whatever its subject matter may be',
        'The picaresque is confined to Spanish literature of the sixteenth century',
        'The picaresque lacks a first-person narrator by convention',
      ],
      0,
      'Lazarillo de Tormes is the origin. The absence of development is structural rather than incidental - the point is a survey of society, not a life.',
    ],
    [
      'What is the argument of Bakhtin\'s "Rabelais and His World"?',
      [
        'Carnival inverts hierarchy through the grotesque body',
        'The novel emerged from the epic through a gradual loosening of metrical constraint',
        'Rabelais\'s obscenity is a coded critique of specific ecclesiastical figures',
        'Popular festival culture derives from surviving pagan agricultural rites',
      ],
      0,
      'Written in the 1930s, published in 1965. Whether carnival is genuinely subversive or a safety valve is the argument it started.',
    ],
    [
      'The "Menippean satire" tradition is characterised by what?',
      [
        'Mixed prose and verse, fantastic settings',
        'Verse invective directed at named contemporary figures',
        'Dialogue between a wise interlocutor and a naive questioner',
        'Allegorical narrative with a strict one-to-one correspondence',
      ],
      0,
      'Lucian, Petronius, and through them Swift and Sterne. Frye and Bakhtin both use the category for very different purposes.',
    ],
    [
      'What does Genette mean by "paratext"?',
      [
        'The apparatus surrounding a text',
        'The intertextual allusions a work makes to earlier works',
        'The variant readings recorded in a critical apparatus',
        'The commentary tradition that accumulates around a canonical work',
      ],
      0,
      'Seuils, 1987. It made the physical book a legitimate object of poetics rather than a matter for bibliographers alone.',
    ],
    [
      'The Chinese "eight-legged essay" was significant as what?',
      [
        'The rigidly prescribed prose form required in the imperial civil service examinations',
        'A classical verse form of eight parallel couplets, required in the poetry section of the examination',
        'A commentarial genre attached to the Confucian classics',
        'A dramatic structure used in Yuan-dynasty theatre',
      ],
      0,
      'Its abolition in 1905 ended a system a millennium old, and it remains a byword in Chinese for stultifying formalism.',
    ],
    [
      'What is "aemulatio" in Renaissance poetics?',
      [
        'Rivalrous imitation that seeks to surpass its model rather than merely reproduce it',
        'The literal translation of a classical text into the vernacular',
        'The attribution of a new work to a classical author',
        'The compilation of commonplaces from approved authorities',
      ],
      0,
      'Imitatio with ambition. It is why "derivative" is an anachronistic charge to bring against sixteenth-century poetry.',
    ],
  ],
  professor: [
    [
      'What does Moretti\'s "distant reading" propose, and what is the standard objection?',
      [
        'Quantitative analysis of large corpora',
        'Close attention to non-canonical texts, objected to for diluting the canon',
        'Reading translations rather than originals, objected to on philological grounds',
        'Analysis of publishing records rather than texts, objected to as book history',
      ],
      0,
      'The interesting version of the objection is not anti-quantitative: it is that the units being counted are themselves interpretive.',
    ],
    [
      'The "New Bibliography" of Greg and Bowers aimed at what?',
      [
        'Reconstructing an author\'s final intentions through eclectic editing of witnesses',
        'Reproducing the earliest surviving printed text without emendation',
        'Recording every variant without adjudicating between them',
        'Establishing the social conditions of a text\'s production',
      ],
      0,
      'McGann and McKenzie\'s socialised-text critique is the reaction, and it is why modern editions are so much likelier to be documentary.',
    ],
    [
      'What is the significance of the Nag Hammadi codices for the study of early Christian literature?',
      [
        'They preserve Coptic translations of Gnostic texts previously known mainly through hostile summaries',
        'They contain the earliest known manuscripts of the canonical gospels',
        'They preserve a Hebrew original of a New Testament book',
        'They record the proceedings of the Council of Nicaea, including the doctrinal debates the surviving canons omit',
      ],
      0,
      'Found in 1945. The Gospel of Thomas is the one that changed how the sayings tradition is reconstructed.',
    ],
    [
      'The "oral-formulaic" theory of Parry and Lord claims what?',
      [
        'Epic diction is built from metrical formulae that permit composition in performance',
        'Epics were memorised verbatim and transmitted without variation',
        'Written epic derives from a single lost oral original in each tradition',
        'Formulaic repetition is a sign of late scribal interpolation',
      ],
      0,
      'Parry\'s Homeric analysis was tested by Lord on South Slavic singers. It reframed "was Homer one poet" as the wrong question.',
    ],
    [
      'What does Spivak\'s "Can the Subaltern Speak?" argue?',
      [
        'That representational structures foreclose subaltern speech',
        'That subaltern testimony is always available if archives are read against the grain',
        'That colonial archives contain no usable information about subordinated groups',
        'That subaltern studies should abandon textual method for oral history',
      ],
      0,
      'The essay is frequently cited as a flat denial, which it is not - the argument is about the conditions of intelligibility.',
    ],
    [
      'What is "eco-criticism" usually taken to have added to literary study?',
      [
        'Attention to the representation of non-human nature',
        'A method for dating texts by their references to climate events',
        'A quantitative index of natural imagery across periods',
        'A requirement that criticism serve environmental advocacy',
      ],
      0,
      'The strongest work in it - Buell, Morton - is largely about what pastoral convention makes it impossible to notice.',
    ],
    [
      'The concept of "world literature" in its recent revival turns on which problem?',
      [
        'How to read across languages without either flattening difference or defaulting to a metropolitan canon',
        'Whether translation is possible in principle between unrelated language families',
        'Whether the novel is a universal or a European form',
        'How to establish a single chronology across incommensurable traditions',
      ],
      0,
      'Damrosch, Casanova and Apter stake out very different answers, and Apter\'s "untranslatability" is the sharpest objection to the whole project.',
    ],
    [
      'What does "textual criticism of the New Testament" mean by an eclectic text?',
      [
        'A reconstructed text adopting readings from different manuscripts on internal and external grounds',
        'A text following one manuscript throughout, emended only where it is unreadable',
        'A text printing the majority reading wherever manuscripts disagree',
        'A text listing all variants without printing a preferred reading',
      ],
      0,
      'The Nestle-Aland edition. Majority-text and single-witness approaches both exist and both have partisans, which is why the choice is a methodological commitment.',
    ],
    [
      'The Sanskrit "dhvani" theory of Anandavardhana holds what?',
      [
        'Poetic meaning works by suggestion beyond the literal and figurative senses',
        'Poetry is distinguished from prose by metrical regularity alone',
        'The purpose of poetry is instruction concealed in pleasure',
        'Figurative language is ornamental and separable from meaning',
      ],
      0,
      'Suggestion as the soul of poetry, against the older ornament-based schools - and a position with no close European counterpart before the symbolists.',
    ],
    [
      'What is the argument of Erich Auerbach\'s "figura" essay?',
      [
        'That figural interpretation reads historical events as prefigurations of later ones, both remaining real',
        'That allegory dissolves the literal sense in favour of the spiritual',
        'That typology was a medieval invention without patristic precedent',
        'That figurative language in scripture is always ornamental, and separable from the doctrinal content it decorates',
      ],
      0,
      'The distinction from allegory is the load-bearing part: in figural reading both terms stay historically real, which is what makes Dante possible.',
    ],
  ],
};
